import { useEffect, useState } from 'react';
import { useQuery, useMutation } from 'urql';
import { useWorkspace } from '@/stores/workspace.store';
import { Q_PROJECTS, Q_BRAND_PROFILE, M_UPDATE_BRAND } from '@/lib/queries';
import { useUi } from '@/stores/ui.store';

export default function BrandRoute() {
  const { toast } = useUi();
  const [projects] = useQuery({ query: Q_PROJECTS });
  const projectId = (useWorkspace.getState().project_id ?? (projects.data?.projects?.find((p:any)=>p.id===useWorkspace.getState().project_id) ?? projects.data?.projects?.[0])?.id);

  const [profile, refetch] = useQuery({ query: Q_BRAND_PROFILE, variables: { projectId }, pause: !projectId });
  const [, save] = useMutation(M_UPDATE_BRAND);

  const [form, setForm] = useState({
    voiceDescription: '', toneOfVoice: '', styleGuidelines: '',
    contentPillars: '', tabooTopics: '', forbiddenWords: '',
    audienceText: '',
  });

  useEffect(() => {
    const b = profile.data?.brandProfile;
    if (!b) return;
    setForm({
      voiceDescription: b.voiceDescription ?? '',
      toneOfVoice: b.toneOfVoice ?? '',
      styleGuidelines: b.styleGuidelines ?? '',
      contentPillars: (b.contentPillars ?? []).join(', '),
      tabooTopics: (b.tabooTopics ?? []).join(', '),
      forbiddenWords: (b.forbiddenWords ?? []).join(', '),
      audienceText: b.targetAudience ? JSON.stringify(b.targetAudience, null, 2) : '',
    });
  }, [profile.data]);

  const submit = async () => {
    if (!projectId) { toast('No project found', 'error'); return; }
    let targetAudience: unknown = null;
    if (form.audienceText.trim()) {
      try { targetAudience = JSON.parse(form.audienceText); }
      catch { toast('Audience must be valid JSON', 'warning'); return; }
    }
    const r = await save({
      input: {
        projectId,
        voiceDescription: form.voiceDescription || null,
        toneOfVoice: form.toneOfVoice || null,
        styleGuidelines: form.styleGuidelines || null,
        targetAudience,
        contentPillars: csv(form.contentPillars),
        tabooTopics: csv(form.tabooTopics),
        forbiddenWords: csv(form.forbiddenWords),
      },
    });
    if (r.error) { toast(r.error.message, 'error'); return; }
    toast('Brand profile saved');
    refetch({ requestPolicy: 'network-only' });
  };

  if (projects.fetching) return <div className="p-8 text-stone-500">Loading…</div>;

  return (
    <div className="p-8 max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-stone-900">Brand kit</h1>
        <p className="text-sm text-stone-500 mt-1">Injected into every AI generation so output is on-brand by default.</p>
      </div>

      <Field label="Voice description" hint="The overall personality of your content.">
        <textarea rows={3} value={form.voiceDescription} onChange={(e) => setForm({ ...form, voiceDescription: e.target.value })}
          placeholder="Confident, conversational, a little nerdy. Respect the reader's time."
          className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none" />
      </Field>

      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Tone of voice">
          <input value={form.toneOfVoice} onChange={(e) => setForm({ ...form, toneOfVoice: e.target.value })}
            placeholder="warm, expert, never preachy"
            className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
        </Field>
        <Field label="Content pillars" hint="comma-separated">
          <input value={form.contentPillars} onChange={(e) => setForm({ ...form, contentPillars: e.target.value })}
            placeholder="career tips, study skills, industry trends"
            className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
        </Field>
      </div>

      <Field label="Target audience (JSON)" hint='e.g. [{"label":"Students","desc":"..."}]'>
        <textarea rows={4} value={form.audienceText} onChange={(e) => setForm({ ...form, audienceText: e.target.value })}
          placeholder='[{"label": "Students", "desc": "undergrad + postgrad"}, {"label": "Educators", "desc": "..."}]'
          className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none" />
      </Field>

      <Field label="Style guidelines">
        <textarea rows={2} value={form.styleGuidelines} onChange={(e) => setForm({ ...form, styleGuidelines: e.target.value })}
          placeholder="Oxford comma. Avoid jargon. Active voice."
          className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none" />
      </Field>

      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Taboo topics" hint="comma-separated">
          <input value={form.tabooTopics} onChange={(e) => setForm({ ...form, tabooTopics: e.target.value })}
            placeholder="job placement rate claims, politics"
            className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
        </Field>
        <Field label="Words to avoid" hint="comma-separated">
          <input value={form.forbiddenWords} onChange={(e) => setForm({ ...form, forbiddenWords: e.target.value })}
            placeholder="revolutionary, game-changer, synergy"
            className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
        </Field>
      </div>

      <div className="flex justify-end">
        <button onClick={submit} className="px-5 py-2.5 bg-violet-600 hover:bg-violet-700 text-white rounded-lg text-sm font-medium">Save brand profile</button>
      </div>
    </div>
  );
}

function csv(s: string): string[] {
  return s.split(',').map((x) => x.trim()).filter(Boolean);
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-stone-600">{label}{hint && <span className="text-stone-400 font-normal"> — {hint}</span>}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
